import { useInfiniteQuery } from "@tanstack/react-query";
import type { InboxAiDraft } from "./use-inbox-drafts";

const BASE = "/api";

export interface InboxComment {
  id: string;
  brandId: string;
  zernioCommentId: string;
  zernioPostId: string;
  parentCommentId: string | null;
  authorId: string;
  authorName: string | null;
  authorAvatar: string | null;
  text: string | null;
  hidden: boolean;
  likedByUs: boolean;
  /** TikTok for Business only. */
  pinnedByUs?: boolean;
  platform?: string | null;
  /** Replies Zernio last reported for this comment. */
  replyCount?: number;
  status: "OPEN" | "SNOOZED" | "DONE" | "ARCHIVED";
  assignedUserId: string | null;
  assignedUser: { id: string; name: string | null; email: string } | null;
  postedAt: string;
  aiDraft?: InboxAiDraft | null;
  post: {
    scheduledPostId: string | null;
    zernioPostId: string;
    thumbnailUrl: string | null;
    pieceTitle: string | null;
  };
}

interface Page {
  comments: InboxComment[];
  nextCursor: string | null;
}

interface Params {
  brandId: string;
  status?: "OPEN" | "SNOOZED" | "DONE" | "ARCHIVED";
  assigned?: string;
  postId?: string;
  platform?: string; // channel filter; undefined = all platforms
}

async function fetchPage(params: Params, cursor?: string): Promise<Page> {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.assigned) search.set("assigned", params.assigned);
  if (params.postId) search.set("postId", params.postId);
  if (params.platform) search.set("platform", params.platform);
  if (cursor) search.set("cursor", cursor);
  const r = await fetch(
    `${BASE}/brands/${params.brandId}/inbox/comments?${search.toString()}`,
    { credentials: "include" },
  );
  if (!r.ok) throw new Error("Failed to load comments");
  const json = (await r.json()) as { data: Page };
  return json.data;
}

export function useInboxComments(params: Params) {
  return useInfiniteQuery({
    queryKey: ["inbox-comments", params.brandId, params.status, params.assigned, params.postId, params.platform],
    queryFn: ({ pageParam }) => fetchPage(params, pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!params.brandId,
    staleTime: 15_000,
  });
}
